import * as moment from 'moment';

import { currencies, type Currency } from '~/constants/currency';
import {
  type BlackbeardSignatureConfirmation,
  type BlackbeardSignatureConfirmationExtra,
} from '~/pages/Orders/Blackbeard/LabelPurchase/modules/types';
import { fromJSON } from '~/types/Parcel';
import { parseBusinessRules } from '~/utils/businessRules';

import type Address from './Address';
import type BusinessRule from './BusinessRule';
import type CustomsDeclaration from './CustomsDeclaration';
import type CustomsItem from './CustomsItem';
import type LineItem from './LineItem';
import type Order from './Order';
import type Parcel from './Parcel';

import * as APIError from './APIError';
import * as address from './Address';
import { type JSONBusinessRule } from './BusinessRule';
import { type JSONCustomsDeclaration, type WebCustomsDeclaration } from './CustomsDeclaration';
import {
  fromJSON as customsDeclarationFromJSON,
  fromWebCustomsDeclaration,
  requiresCustomsDeclaration as isCustomsRequired,
} from './CustomsDeclaration';
import { type JSONCustomsItem } from './CustomsItem';
import { fromJSON as customsItemFromJSON, fromWebCustomsItem } from './CustomsItem';
import { type WebShipmentLineItemsInner } from './LineItem';
import {
  BatchEntryStatus,
  batchEntriesFromJSON,
  type BatchEntry,
  type JSONBatchEntry,
  type UserBatchInput,
} from './OrdersBatch';
import { type JSONParcel, type WebParcel } from './Parcel';
import { fromWebParcel } from './Parcel';
import { type WebRate } from './Rate';

export type RecipientType = 'CONSUMER' | 'LICENSEE';

interface JSONInsuranceExtra {
  amount: Maybe<string>;
  content: string;
  currency: string;
  use_imported_amount: boolean;
}

interface JSONAlcoholExtra {
  contains_alcohol: boolean;
  recipient_type: Maybe<RecipientType>;
}

interface JSONDryIceExtra {
  contains_dry_ice: boolean;
  weight: string;
  weight_unit_id: number;
}

interface JSONHazmatExtra {
  contains: boolean;
  contains_biological_material: boolean;
  contains_lithium_batteries: boolean;
}

export interface JSONShipmentExtras {
  alcohol?: Maybe<JSONAlcoholExtra>;
  dangerous_goods?: Maybe<JSONHazmatExtra>;
  dry_ice?: Maybe<JSONDryIceExtra>;
  insurance?: Maybe<JSONInsuranceExtra>;
  is_bypass_address_validation?: boolean;
  is_bypass_customs?: boolean;
  is_residential?: boolean;
  is_return_requested?: boolean;
  packing_slip_text: Maybe<string>;
  signature_confirmation?: Maybe<BlackbeardSignatureConfirmation>;
}

export interface JSONBatchEntryExtended extends JSONBatchEntry {
  address_from?: address.JSONAddress;
  address_return?: address.JSONAddress;
  address_to?: address.JSONAddress;
  customs_declaration?: JSONCustomsDeclaration;
  customs_items?: JSONCustomsItem[];
  errors?: APIError.JSONAPIError[];
  extras?: JSONShipmentExtras;
  is_bypass_customs?: boolean;
  line_items?: JSONLineItemLite[];
  parcel?: JSONParcel;
  parcels?: JSONParcel[];
  partial_fulfillment_rank: number;
  preferred_account_object_id?: string;
  preferred_service_level_token?: string;
  rules?: JSONBusinessRule[];
  shipment_date: Maybe<string>;
  shipment_object_id: Maybe<string>;
  transaction_object_id: Maybe<string>;
  use_imported_weight: Maybe<boolean>;
}

type WebExtrasAlcohol = {
  contains_alcohol?: boolean;
  recipient_type?: 'CONSUMER' | 'LICENSEE' | null;
};

type WebExtrasDryIce = {
  contains_dry_ice?: boolean;
  weight?: number;
  weight_unit_id?: string;
};

type WebExtrasInsurance = {
  amount: string;
  claim_id?: string;
  content?: string;
  currency?: string;
  provider?: string;
  use_imported_amount?: boolean;
};

type WebExtrasDangerousGoods = {
  contains: boolean;
  contains_biological_material: boolean;
  contains_lithium_batteries: boolean;
};

export type WebExtras = {
  alcohol: WebExtrasAlcohol;
  dangerous_goods: WebExtrasDangerousGoods;
  dry_ice: WebExtrasDryIce;
  insurance: WebExtrasInsurance;
  is_bypass_address_validation: boolean;
  is_bypass_customs: boolean;
  is_return_requested: boolean;
  packing_slip_text: string;
  signature_confirmation: string;
};

export type BaseWebShipment = {
  address_from: address.WebAddress;
  address_return: address.WebAddress;
  address_to: address.WebAddress;
  alternate_address_to: string;
  customs_declaration?: WebCustomsDeclaration;
  extras?: WebExtras;
  id: string;
  is_return: boolean;
  object_created: Date;
  object_owner: string;
  object_updated: Date;
  order_id?: string;
  parcels: WebParcel[];
  preferred_account_object_id: string;
  preferred_service_level_token: string;
  return_of_shipment_id?: string;
  shipment_date: string;
  status: string;
};

export type WebShipment = {
  carrier_accounts?: object[];
  line_items: WebShipmentLineItemsInner[];
  rates: WebRate[];
  transaction: string;
} & BaseWebShipment;

export interface InsuranceExtra {
  amount: string;
  currency: Currency;
  useImportedAmount: boolean;
}

interface AlcoholExtra {
  containsAlcohol: boolean;
  recipientType: Maybe<RecipientType>;
}

export interface DryIceExtra {
  containsDryIce: boolean;
  weight: string;
  weightUnitId: number;
}

interface HazmatExtra {
  isBiologicalMaterial: boolean;
  isHazmat: boolean;
  isLithiumBatteries: boolean;
}

export interface ShipmentExtras {
  alcohol: Maybe<AlcoholExtra>;
  dryIce: Maybe<DryIceExtra>;
  hazmat: Maybe<HazmatExtra>;
  insurance: Maybe<InsuranceExtra>;
  isBypassAddressValidation: boolean;
  isBypassCustoms: boolean;
  isReturnRequested?: boolean;
  packingSlipText: Maybe<string>;
  signatureConfirmation: Maybe<BlackbeardSignatureConfirmationExtra>;
}

export interface BatchEntryExtended extends BatchEntry {
  addressFrom: Maybe<Address>;
  addressReturn: Maybe<Address>;
  addressTo: Maybe<Address>;
  customsDeclaration?: CustomsDeclaration;
  customsDeclarationId?: Maybe<number>;
  customsItems?: CustomsItem[];
  errors: APIError.APIError[];
  extras?: ShipmentExtras;
  lineItems: LineItemLite[];
  parcel?: Maybe<Parcel>;
  partialFulfillmentRank: number;
  preferredAccountObjectID?: string;
  preferredServiceLevelToken?: string;
  rules: BusinessRule[];
  shipmentDate: Maybe<moment.Moment>;
  shipmentObjectId: Maybe<string>;
  transactionObjectId: Maybe<string>;
  useImportedWeight: boolean;
}

export interface PartialFulfillmentItemContent {
  lineItem: string;
  quantity: number;
  shouldDisableSplit?: boolean;
}

export type PartialFulfillmentContents = PartialFulfillmentItemContent[];

export type JSONBatchEntriesResponse = {
  data: JSONBatchEntryExtended[];
};

interface JSONPartialFulfillmentItemContent {
  line_item: string;
  quantity: number;
}

export type JSONPartialFulfillmentContents = JSONPartialFulfillmentItemContent[];

export const partialFulfillmentItemContentToJSON = ({
  lineItem,
  quantity,
}: PartialFulfillmentItemContent): JSONPartialFulfillmentItemContent => ({
  line_item: lineItem,
  quantity: Number(quantity),
});

export type JSONLineItemLite = {
  object_id: string;
  quantity: number;
};

export type LineItemLite = {
  objectId: string;
  quantity: number;
};

const lineItemLiteFromJSON = ({ object_id, quantity }: JSONLineItemLite): LineItemLite => ({
  objectId: object_id,
  quantity,
});

export const fromWebShipment = (shipment: WebShipment, orderId: string): BatchEntryExtended => {
  const firstParcel: WebParcel | undefined =
    shipment.parcels.length > 0 ? shipment.parcels[0] : undefined;
  const hasSignatureConfirmation = (shipment.extras?.signature_confirmation || '').length > 0;
  const customsItems = shipment.customs_declaration?.items?.map(fromWebCustomsItem);
  const userInput: UserBatchInput = {
    addressFrom: address.fromWebAddress(shipment.address_from),
    addressReturn: address.fromWebAddress(shipment.address_return),
    extra: {
      insurance: {
        amount: shipment.extras?.insurance.amount || '',
        content: shipment.extras?.insurance.content || '',
        currency: shipment.extras?.insurance.currency || 'USD',
        useImportedOrderValue: shipment.extras?.insurance.use_imported_amount || false,
      },
      isResidential: shipment.address_to.is_residential,
      isReturn: shipment.is_return,
      signatureConfirmation: hasSignatureConfirmation,
    },
    parcel: {
      distanceUnit: firstParcel?.distance_unit || 'in',
      height: firstParcel?.height || '',
      // a unique parcel type specific to userBatchInput
      length: firstParcel?.length || '',
      templateToken: firstParcel?.carrier_parcel_template?.token,
      useImportedOrderWeight: firstParcel?.use_item_weight || false,
      userParcelTemplateId: firstParcel?.user_parcel_template?.object_id,
      weight: firstParcel?.weight || '',
      weightUnit: firstParcel?.mass_unit || 'lb',
      width: firstParcel?.width || '',
    },
    shipmentDate: moment(shipment.shipment_date),
  };

  const extras: ShipmentExtras = {
    alcohol: {
      containsAlcohol: shipment.extras?.alcohol.contains_alcohol || false,
      recipientType: shipment.extras?.alcohol.recipient_type || 'CONSUMER',
    },
    dryIce: {
      containsDryIce: shipment.extras?.dry_ice.contains_dry_ice || false,
      weight: shipment.extras?.dry_ice.weight?.toString() || '',
      weightUnitId: Number(shipment.extras?.dry_ice.weight_unit_id),
    },
    //nulls
    hazmat: {
      isBiologicalMaterial: false,
      isHazmat: false,
      isLithiumBatteries: false,
    },
    insurance: {
      amount: shipment.extras?.insurance.amount || '',
      currency: shipment.extras?.insurance.currency
        ? currencies[shipment.extras.insurance.currency]
        : currencies.USD,
      useImportedAmount: shipment.extras?.insurance.use_imported_amount || false,
    },
    isBypassAddressValidation: shipment.extras?.is_bypass_address_validation || false,
    isBypassCustoms: shipment.extras?.is_bypass_customs || false,
    isReturnRequested: shipment.extras?.is_return_requested,
    packingSlipText: shipment.extras?.packing_slip_text,
    signatureConfirmation: {
      hasSignatureConfirmation,
      signatureConfirmationType:
        shipment.extras && <BlackbeardSignatureConfirmation>shipment.extras.signature_confirmation,
    },
  };

  const lineItems = shipment.line_items.map((it) => ({
    objectId: it.object_id,
    quantity: it.quantity,
  }));

  return {
    addressFrom: address.fromWebAddress(shipment.address_from),
    addressReturn: address.fromWebAddress(shipment.address_return),
    addressTo: address.fromWebAddress(shipment.address_to),
    customsDeclaration:
      shipment.customs_declaration && fromWebCustomsDeclaration(shipment.customs_declaration),
    customsDeclarationId: undefined,
    customsItems,
    errors: [],
    extras,
    //nulls
    id: 0,
    isReturn: shipment.is_return,
    lineItems,
    objectCreated: moment(shipment.object_created),
    objectStatus: <BatchEntryStatus>shipment.status,
    objectUpdated: moment(shipment.object_updated),
    orderObjectId: orderId,
    parcel: firstParcel && fromWebParcel(firstParcel),
    partialFulfillmentRank: 0,
    preferredAccountObjectID: shipment.preferred_account_object_id,
    preferredServiceLevelToken: shipment.preferred_service_level_token,
    rules: [],

    shipmentDate: moment(shipment.shipment_date),
    shipmentObjectId: shipment.id,
    transactionObjectId: shipment.transaction,
    useImportedWeight: firstParcel?.use_item_weight || false,
    userInput,
  };
};

export const getBatchEntryLineItemsTotalWeight = (
  beLineItems: LineItemLite[],
  lineItems: LineItem[],
) =>
  beLineItems
    ?.reduce((summedWeight, lineItemLite) => {
      const lineItem = lineItems.find((x) => x.objectId === lineItemLite.objectId);
      return lineItem
        ? summedWeight + parseFloat(lineItem.unitWeight) * lineItemLite.quantity
        : summedWeight;
    }, 0)
    .toFixed(2);

export const getBatchEntryLineItemsTotalValue = (
  beLineItems: LineItemLite[],
  lineItems: LineItem[],
) =>
  beLineItems
    .reduce((summedTotal, lineItemLite) => {
      const lineItem = lineItems.find((x) => x.objectId === lineItemLite.objectId);
      return lineItem
        ? summedTotal + parseFloat(lineItem.unitAmount) * lineItemLite.quantity
        : summedTotal;
    }, 0)
    .toFixed(2);

export const getBatchEntryCustomItemsTotalWeight = (
  batchEntry: Maybe<BatchEntryExtended>,
): number => {
  if (!batchEntry || !batchEntry.customsItems) return 0;

  const totalWeight = batchEntry?.customsItems
    .reduce((summedWeight, item) => summedWeight + parseFloat(item.convertedNetWeight), 0)
    .toFixed(2);
  return parseFloat(totalWeight);
};

const extrasFromJSON = (jsonShipmentExtras: JSONShipmentExtras): ShipmentExtras => ({
  alcohol: jsonShipmentExtras.alcohol
    ? {
        containsAlcohol: jsonShipmentExtras.alcohol.contains_alcohol,
        recipientType: jsonShipmentExtras.alcohol.recipient_type,
      }
    : null,
  dryIce: jsonShipmentExtras.dry_ice
    ? {
        containsDryIce: jsonShipmentExtras.dry_ice.contains_dry_ice,
        weight: jsonShipmentExtras.dry_ice.weight,
        weightUnitId: jsonShipmentExtras.dry_ice.weight_unit_id,
      }
    : null,
  hazmat: jsonShipmentExtras.dangerous_goods
    ? {
        isBiologicalMaterial: !!jsonShipmentExtras.dangerous_goods.contains_biological_material,
        isHazmat: !!jsonShipmentExtras.dangerous_goods.contains,
        isLithiumBatteries: !!jsonShipmentExtras.dangerous_goods.contains_lithium_batteries,
      }
    : null,
  insurance: jsonShipmentExtras.insurance
    ? {
        amount: jsonShipmentExtras.insurance.amount ? jsonShipmentExtras.insurance.amount : '',
        currency: currencies[jsonShipmentExtras.insurance.currency] || currencies.USD,
        useImportedAmount: jsonShipmentExtras.insurance.use_imported_amount,
      }
    : null,
  isBypassAddressValidation: !!jsonShipmentExtras.is_bypass_address_validation,
  isBypassCustoms: !!jsonShipmentExtras.is_bypass_customs,
  isReturnRequested: jsonShipmentExtras.is_return_requested || false,
  packingSlipText: jsonShipmentExtras.packing_slip_text,
  signatureConfirmation: jsonShipmentExtras.signature_confirmation
    ? {
        hasSignatureConfirmation: !!jsonShipmentExtras.signature_confirmation,
        signatureConfirmationType:
          jsonShipmentExtras.signature_confirmation as BlackbeardSignatureConfirmation,
      }
    : null,
});

export const batchEntryExtendedFromJSON = (
  jsonBatchEntry: JSONBatchEntryExtended,
): BatchEntryExtended => {
  const batchEntry = batchEntriesFromJSON(jsonBatchEntry);

  const webOrderBatchEntryParcel = jsonBatchEntry.parcels?.length
    ? fromJSON(jsonBatchEntry.parcels[0])
    : undefined;

  return {
    ...batchEntry,
    addressFrom: jsonBatchEntry.address_from ? address.fromJSON(jsonBatchEntry.address_from) : null,
    addressReturn: jsonBatchEntry.address_return
      ? address.fromJSON(jsonBatchEntry.address_return)
      : null,
    addressTo: jsonBatchEntry.address_to ? address.fromJSON(jsonBatchEntry.address_to) : null,
    customsDeclaration: jsonBatchEntry.customs_declaration
      ? customsDeclarationFromJSON(jsonBatchEntry.customs_declaration)
      : undefined,
    customsItems: jsonBatchEntry.customs_items
      ? jsonBatchEntry.customs_items.map(customsItemFromJSON)
      : undefined,
    errors: jsonBatchEntry?.errors?.map(APIError.fromJSON) || [],
    extras: jsonBatchEntry.extras ? extrasFromJSON(jsonBatchEntry.extras) : undefined,
    id: Number(jsonBatchEntry.id),
    lineItems: jsonBatchEntry.line_items ? jsonBatchEntry.line_items.map(lineItemLiteFromJSON) : [],
    parcel: jsonBatchEntry.parcel ? fromJSON(jsonBatchEntry.parcel) : webOrderBatchEntryParcel,
    partialFulfillmentRank: jsonBatchEntry.partial_fulfillment_rank,
    preferredAccountObjectID: jsonBatchEntry.preferred_account_object_id,
    preferredServiceLevelToken: jsonBatchEntry.preferred_service_level_token,
    rules: parseBusinessRules(jsonBatchEntry?.rules || new Array<JSONBusinessRule>()).businessRules,
    shipmentDate: jsonBatchEntry.shipment_date ? moment(jsonBatchEntry.shipment_date) : null,
    shipmentObjectId: jsonBatchEntry.shipment_object_id,
    transactionObjectId: jsonBatchEntry.transaction_object_id,
    useImportedWeight: !!jsonBatchEntry.use_imported_weight,
  };
};

export const extrasToJSON = (extras: Partial<ShipmentExtras>): JSONShipmentExtras => ({
  alcohol:
    extras.alcohol && extras.alcohol.containsAlcohol
      ? {
          contains_alcohol: extras.alcohol.containsAlcohol,
          recipient_type: extras.alcohol.recipientType,
        }
      : extras.alcohol === null
      ? null
      : undefined,
  dangerous_goods: extras.hazmat
    ? {
        contains: extras.hazmat.isHazmat,
        contains_biological_material: extras.hazmat.isBiologicalMaterial,
        contains_lithium_batteries: extras.hazmat.isLithiumBatteries,
      }
    : extras.hazmat,
  dry_ice: extras.dryIce
    ? {
        contains_dry_ice: extras.dryIce.containsDryIce,
        weight: extras.dryIce.weight,
        weight_unit_id: extras.dryIce.weightUnitId,
      }
    : extras.dryIce,
  insurance: extras.insurance
    ? {
        amount: !extras.insurance.useImportedAmount ? extras.insurance.amount : undefined,
        content: '',
        currency: extras.insurance.currency.iso,
        use_imported_amount: extras.insurance.useImportedAmount,
      }
    : extras.insurance,
  is_bypass_address_validation: extras.isBypassAddressValidation,
  is_bypass_customs: extras.isBypassCustoms,
  is_return_requested: extras.isReturnRequested,
  packing_slip_text: extras.packingSlipText,
  signature_confirmation: extras.signatureConfirmation?.hasSignatureConfirmation
    ? extras.signatureConfirmation.signatureConfirmationType
    : extras.signatureConfirmation === null
    ? null
    : undefined,
});

export const isReadyForPurchase = (batchEntry: Maybe<BatchEntryExtended>): boolean =>
  !!batchEntry &&
  !!batchEntry.shipmentObjectId &&
  batchEntry.objectStatus === BatchEntryStatus.COMPLETE_SHIPMENT;

export const isPartialFulfillmentRequest = (batchEntry: BatchEntryExtended): boolean =>
  batchEntry.partialFulfillmentRank > 0;

export const requiresCustomsDeclaration = (batchEntry: BatchEntryExtended): boolean => {
  const { addressFrom, addressTo } = batchEntry;
  if (!addressTo || !addressFrom) return false;
  return isCustomsRequired(addressTo, addressFrom);
};

// Returns batch entries ids sorted in ascending rank order
export const getBatchEntriesIdsSortedByRank = (
  batchEntryIds: number[],
  batchEntries: HashMap<number, BatchEntryExtended>,
): number[] =>
  batchEntryIds
    .map((beId) => batchEntries[beId])
    .filter((be) => !!be)
    .sort((a, b) => a.partialFulfillmentRank - b.partialFulfillmentRank)
    .map((be) => be.id);

export const requiresNewRates = (
  batchEntry?: {
    objectStatus: BatchEntryExtended['objectStatus'] | null;
    parcel?: BatchEntryExtended['parcel'] | boolean;
    shipmentObjectId: BatchEntryExtended['shipmentObjectId'] | null;
  } | null,
) => {
  if (!batchEntry) return true;

  return (
    batchEntry.objectStatus === BatchEntryStatus.SHIPMENT_REQUEST_FAILED ||
    (batchEntry.objectStatus === BatchEntryStatus.PENDING &&
      !batchEntry.shipmentObjectId &&
      !!batchEntry.parcel)
  );
};

export const getTransactionFor = (order: Maybe<Order>, batchEntry: Maybe<BatchEntryExtended>) => {
  if (!order) return;

  if (batchEntry) {
    return order.transactions.find(
      (transaction) => transaction.objectId === batchEntry.transactionObjectId,
    );
  }

  return order.transactions[order.transactions.length - 1];
};
